import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "./About.css";

const About: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const [contentRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(
    scrollYProgress,
    [0, 0.3, 0.7, 1],
    [0.8, 1, 1, 0.8]
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
      },
    },
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="about" className="about-section" ref={ref}>
      {/* Background Image with Parallax */}
      <motion.div
        className="about-background"
        style={{
          y,
          opacity,
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5)), url('about.webp')`,
        }}
      />

      <div className="container" ref={contentRef}>
        <motion.div
          className="about-content"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Header */}
          <motion.div className="about-header" variants={itemVariants}>
            <h2>
              Deep card strategy and tactical hex combat
              <br />
              in a dark, high fantasy world torn by power.
            </h2>
          </motion.div>

          {/* Main Content */}
          <motion.div className="about-main" variants={itemVariants}>
            <div className="about-text">
              <p>
                A celestial artifact has mysteriously fractured and scattered countless shards of magical power across the lands of Imriel. Contenders have emerged from all corners of the realm to seize these shards for their own ends, motivated by ambition, duty, greed, or darker purpose. These Seekers, who range from shadowy assassins and holy knights to scholarly mages and demonic warlocks, clash across Imriel in brutal combat, each striving to claim the shards for themselves.
              </p>

              <p>
                <span><b>Seekers: Shards of Imriel</b></span> is a tactical card game where players command a single powerful hero on a hex-grid battlemap where range, positioning, and line of sight matter. Players choose a Seeker, outfit them with powerful equipment, and build a deck of abilities, spells, and items tailored to their strategy and their Seeker's strengths. It blends the depth and deck-building of Flesh and Blood and Magic: The Gathering with the positional strategy of games like Dungeons & Dragons and World of Warcraft, offering deeply interactive and competitive PvP gameplay in a rich fantasy world.
              </p>
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div className="about-cta" variants={itemVariants}>
            <button
              onClick={() => scrollToSection("gameplay")}
              className="btn about-btn"
            >
              GAMEPLAY
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
